/**
 * NO CONFORMIDADES DE UNA OT — FASE 4.3-C
 *
 * Lectura y ciclo completo (crear / iniciar tratamiento / cerrar / anular)
 * sobre los endpoints ya existentes de FASE 4.2-B — ningún concepto nuevo
 * de backend. La máquina de estados (ABIERTA/EN_TRATAMIENTO/CERRADA/
 * ANULADA) vive en `fn_nc_transicion` (0032); `puedeAccionNc()` de aquí
 * abajo es sólo un hint de UX que refleja esa máquina para decidir qué
 * botón mostrar — el servidor revalida siempre y puede rechazar.
 *
 * UNA SOLA REPRESENTACIÓN, NO TABLA+TARJETAS
 *
 * A diferencia de las líneas de OT (FASE 4.3-A), una NC no es
 * naturalmente tabular: tiene descripción libre, líneas afectadas
 * variables e histórico de longitud variable. En vez de duplicar
 * tabla-escritorio + tarjeta-móvil (como allí), aquí hay una única lista
 * de tarjetas/acordeón que se adapta por CSS — evita tanto la "tabla
 * horizontal gigante" en móvil como la duplicación de DOM que en 4.3-A
 * obligó a disciplina extra en los tests.
 *
 * RECARGA SIEMPRE DESDE EL SERVIDOR
 *
 * Tras crear una NC o transicionarla, nunca se simula el resultado en el
 * cliente: se relee la propia NC (si su detalle está abierto), la lista
 * de NC de la OT, y la OT misma (`recargarOt`) — así una NC que cierra la
 * OT (CERRADA) o que la reabre (REABIERTA con una NC nueva sobre una OT
 * ya cerrada) se refleja siempre con el estado real que devuelve el
 * backend, nunca con una inferencia local del ciclo NC⇄OT.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import {
  api,
  type DatosCrearNc,
  type DatosTransicionNc,
  type FichaCalidadNc,
  type GravedadNc,
  type NcDetalle,
  type NcFila,
  type OrigenNcSolicitado,
  type OtDetalle,
  type ResultadoCrearNc,
  type ResultadoTransicionNc,
} from '../api.ts';
import { useSesion } from '../contexto.tsx';
import {
  ClasificacionNc,
  FichaCalidadNcSeccion,
  LineaDeTiempo,
  SelectorResponsable,
  textoPercepcionCliente,
  useResponsables,
} from './calidad.tsx';
import { Evidencias } from './evidencias.tsx';
import {
  Cargando,
  Error as ErrorCaja,
  EstadoNc,
  etiquetaEstadoNc,
  fecha,
  fechaHora,
  useDatos,
  Vacio,
} from './base.tsx';

const ETIQUETAS_GRAVEDAD: Record<string, string> = { LEVE: 'Leve', GRAVE: 'Grave', CRITICA: 'Crítica' };

function Dato({ e, v }: { e: string; v: string }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v}</div>
    </div>
  );
}

export function SeccionNoConformidades({
  ot,
  recargarOt,
  lineaPreseleccionada = null,
  alCerrarPreseleccion,
}: {
  ot: OtDetalle;
  recargarOt: () => void;
  /** F10.0: abre el formulario con esta línea (No OK) ya marcada — y lo trae a la vista. */
  lineaPreseleccionada?: string | null;
  alCerrarPreseleccion?: () => void;
}): ReactNode {
  const { puede } = useSesion();
  const puedeLeer = puede('nc:leer');
  //  Sin `nc:leer` (hoy sólo OPERARIO) esta sección no pinta nada, y
  //  tampoco lanza la petición que el servidor rechazaría con 403 —
  //  `ots:leer` tampoco lo tiene OPERARIO, así que en producción ni
  //  siquiera llegaría a esta página; este guard es higiene, no la
  //  barrera real (esa vive en `exigir(pet, 'nc:leer')`, servidor.ts).
  const estadoLista = useDatos(
    () => (puedeLeer ? api.get<{ filas: NcFila[] }>(`/api/ots/${ot.id}/no-conformidades`) : Promise.resolve({ filas: [] })),
    [ot.id, puedeLeer],
  );
  const [creando, setCreando] = useState(false);
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);
  const refSeccion = useRef<HTMLDivElement>(null);
  const puedeCrear = puede('nc:crear');

  useEffect(() => {
    if (lineaPreseleccionada === null || !puedeCrear) return;
    setCreando(true);
    setMensajeExito(null);
    refSeccion.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [lineaPreseleccionada, puedeCrear]);

  if (!puedeLeer) return null;

  function terminarCreacion(): void {
    setCreando(false);
    alCerrarPreseleccion?.();
  }

  function alCambiar(): void {
    estadoLista.recargar();
    recargarOt();
  }

  return (
    <div className="panel nc-seccion" ref={refSeccion}>
      <div className="panel-cabecera">
        <h2>No conformidades</h2>
        {puede('nc:crear') && !creando && (
          <button
            type="button"
            className="pequeno"
            onClick={() => {
              setCreando(true);
              setMensajeExito(null);
            }}
          >
            Nueva no conformidad
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        {mensajeExito !== null && !creando && (
          <div className="nota exito" role="status">
            {mensajeExito}
          </div>
        )}
        {creando && (
          <FormularioCrearNc
            key={lineaPreseleccionada ?? 'ot'}
            ot={ot}
            lineaPreseleccionada={lineaPreseleccionada}
            cancelar={terminarCreacion}
            alCrear={(codigo) => {
              terminarCreacion();
              setMensajeExito(`No conformidad ${codigo} creada.`);
              alCambiar();
            }}
          />
        )}
        {estadoLista.cargando && estadoLista.datos === null && <Cargando texto="Cargando no conformidades…" />}
        {estadoLista.error !== null && <ErrorCaja error={estadoLista.error} reintentar={estadoLista.recargar} />}
        {estadoLista.datos !== null &&
          (estadoLista.datos.filas.length === 0 ? (
            <Vacio titulo="Sin no conformidades registradas" />
          ) : (
            <ul className="lista-nc">
              {estadoLista.datos.filas.map((f) => (
                <TarjetaNc key={f.id} resumen={f} onCambiada={alCambiar} />
              ))}
            </ul>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- crear

function FormularioCrearNc({
  ot,
  lineaPreseleccionada,
  cancelar,
  alCrear,
}: {
  ot: OtDetalle;
  lineaPreseleccionada: string | null;
  cancelar: () => void;
  alCrear: (codigo: string) => void;
}): ReactNode {
  //  F10.0: desde una línea No OK, la NC nace acotada a esa línea y con su motivo como punto de partida.
  const lineaNoOk = lineaPreseleccionada === null ? undefined : ot.lineas.find((l) => l.id === lineaPreseleccionada);
  const { usuario, puede } = useSesion();
  const esCliente = usuario !== null && usuario.idCliente !== null;
  //  FASE 7.2: responsable y fecha objetivo OPCIONALES al crear, sólo para
  //  quien gestiona la NC — el flujo rápido del portal no cambia.
  const conSeguimiento = !esCliente && puede('nc:tratar');
  const responsables = useResponsables();
  const [idResponsable, setIdResponsable] = useState('');
  const [fechaObjetivo, setFechaObjetivo] = useState('');
  const idBase = useId();
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  const [descripcion, setDescripcion] = useState(() =>
    lineaNoOk === undefined
      ? ''
      : `No OK en la línea ${String(lineaNoOk.orden)} (${lineaNoOk.ubicacionCodigo})${lineaNoOk.eventoRelevante?.motivo ? `: ${lineaNoOk.eventoRelevante.motivo}` : ''}`,
  );
  const [gravedad, setGravedad] = useState<GravedadNc>('LEVE');
  const [origen, setOrigen] = useState<OrigenNcSolicitado>('INTERNA');
  const [alcance, setAlcance] = useState<'TODA_OT' | 'LINEAS'>(lineaNoOk === undefined ? 'TODA_OT' : 'LINEAS');
  const [lineasSeleccionadas, setLineasSeleccionadas] = useState<ReadonlySet<string>>(
    () => new Set(lineaNoOk === undefined ? [] : [lineaNoOk.id]),
  );
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function alternarLinea(id: string): void {
    setLineasSeleccionadas((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }

  async function enviar(): Promise<void> {
    if (descripcion.trim() === '') {
      setError(new globalThis.Error('La descripción es obligatoria.'));
      return;
    }
    if (alcance === 'LINEAS' && lineasSeleccionadas.size === 0) {
      setError(new globalThis.Error('Elige al menos una línea, o cambia el alcance a «Toda la OT».'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const datos: DatosCrearNc = {
        idOt: ot.id,
        descripcion,
        gravedad,
        idsLineas: alcance === 'LINEAS' ? [...lineasSeleccionadas] : undefined,
        idOperacionCliente,
        ...(esCliente ? {} : { origen }),
        ...(conSeguimiento && idResponsable !== '' ? { idResponsable } : {}),
        ...(conSeguimiento && fechaObjetivo !== '' ? { fechaObjetivo } : {}),
      };
      const resultado = await api.post<ResultadoCrearNc>('/api/no-conformidades', datos);
      alCrear(resultado.codigo);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea nc-formulario">
      <div className="panel-cabecera">
        <h3>Nueva no conformidad</h3>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-desc`}>Descripción</label>
          <textarea
            id={`${idBase}-desc`}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={3}
          />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-grav`}>Gravedad</label>
            <select id={`${idBase}-grav`} value={gravedad} onChange={(e) => setGravedad(e.target.value as GravedadNc)}>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
            </select>
          </div>
          {conSeguimiento && (
            <>
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-resp`}>Responsable (opcional)</label>
                <SelectorResponsable id={`${idBase}-resp`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} />
              </div>
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-fec`}>Fecha objetivo (opcional)</label>
                <input id={`${idBase}-fec`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
              </div>
            </>
          )}
          {!esCliente && (
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-origen`}>Origen</label>
              <select
                id={`${idBase}-origen`}
                value={origen}
                onChange={(e) => setOrigen(e.target.value as OrigenNcSolicitado)}
              >
                <option value="INTERNA">Empresa</option>
                <option value="AUDITORIA">Auditoría</option>
              </select>
            </div>
          )}
        </div>
        <div className="campo">
          <span className="etiqueta-dato">Alcance</span>
          <div className="fila" style={{ gap: 16 }}>
            <label className="fila" style={{ gap: 6 }}>
              <input
                type="radio"
                name={`${idBase}-alcance`}
                checked={alcance === 'TODA_OT'}
                onChange={() => setAlcance('TODA_OT')}
              />
              Toda la OT
            </label>
            <label className="fila" style={{ gap: 6 }}>
              <input
                type="radio"
                name={`${idBase}-alcance`}
                checked={alcance === 'LINEAS'}
                onChange={() => setAlcance('LINEAS')}
              />
              Líneas concretas
            </label>
          </div>
        </div>
        {alcance === 'LINEAS' && (
          <div className="lista-checks">
            {ot.lineas.map((l) => (
              <label key={l.id} className="fila" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={lineasSeleccionadas.has(l.id)}
                  onChange={() => alternarLinea(l.id)}
                />
                <span className="cod">{l.ubicacionCodigo}</span>
                <span className="pequena suave">{l.descripcionTarea ?? l.tipoDescripcion}</span>
              </label>
            ))}
          </div>
        )}
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Creando…' : 'Crear no conformidad'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- lectura

/**
 * F11.1 — ORIGEN y VISIBILIDAD son dos cosas distintas y nunca comparten
 * palabra: el origen dice quién la detectó (Empresa / Cliente / Auditoría);
 * la visibilidad, si el cliente la ve (Visible para cliente / Solo interna).
 */
export const ETIQUETAS_ORIGEN_NC: Record<string, string> = { INTERNA: 'Empresa', CLIENTE: 'Cliente', AUDITORIA: 'Auditoría' };

export function OrigenNc({ origen }: { origen: string }): ReactNode {
  return <span className={`etiqueta ${origen === 'CLIENTE' ? 'aviso' : 'neutra'}`}>Origen: {ETIQUETAS_ORIGEN_NC[origen] ?? origen}</span>;
}

export function VisibilidadNc({ visible }: { visible: boolean }): ReactNode {
  return <span className={`etiqueta ${visible ? 'info' : 'neutra'}`}>{visible ? 'Visible para cliente' : 'Solo interna'}</span>;
}

/** «Abierta hoy» / «Abierta hace 3 días» (días que calcula el servidor, hoy local del centro) / «Cerrada el …». */
export function textoAntiguedadNc(nc: Pick<NcFila, 'estado' | 'diasAbierta' | 'fechaCierre'>): string | null {
  if (nc.estado === 'CERRADA' && nc.fechaCierre != null) return `Cerrada el ${fecha(nc.fechaCierre)}`;
  if (nc.diasAbierta == null) return null;
  if (nc.diasAbierta <= 0) return 'Abierta hoy';
  return nc.diasAbierta === 1 ? 'Abierta hace 1 día' : `Abierta hace ${String(nc.diasAbierta)} días`;
}

/** «OT-00000337 · línea ENV-LIN-1 y 2 más · escalada desde INC-000002» / «NC del centro (sin OT)». */
export function textoContextoNc(nc: Pick<NcFila, 'otCodigo' | 'lineasCodigos' | 'incidenciaOrigen'>): string {
  const lineas = nc.lineasCodigos ?? [];
  const primera = lineas[0];
  const ot = nc.otCodigo === null
    ? 'NC del centro (sin OT)'
    : `${nc.otCodigo}${primera === undefined ? '' : ` · línea ${primera}${lineas.length > 1 ? ` y ${String(lineas.length - 1)} más` : ''}`}`;
  return nc.incidenciaOrigen != null ? `${ot} · escalada desde ${nc.incidenciaOrigen}` : ot;
}

/** «Abierta por el cliente · Carla …» / «Creada por Elena …». */
export function textoAutorNc(nc: Pick<NcFila, 'origen' | 'creadoPorNombre'>): string {
  const nombre = nc.creadoPorNombre ?? '—';
  return nc.origen === 'CLIENTE' ? `Abierta por el cliente · ${nombre}` : `Creada por ${nombre}`;
}

function TarjetaNc({
  resumen,
  onCambiada,
}: {
  resumen: NcFila;
  onCambiada: () => void;
}): ReactNode {
  const [abierta, setAbierta] = useState(false);

  return (
    <li className="nc-tarjeta">
      <button
        type="button"
        className="nc-tarjeta-cabecera"
        aria-expanded={abierta}
        onClick={() => setAbierta((v) => !v)}
      >
        <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="mono">{resumen.codigo}</span>
          <EstadoNc estado={resumen.estado} />
          {resumen.gravedad === null
            ? <span className="etiqueta aviso">Pendiente de clasificar</span>
            : <span className="etiqueta neutra">{ETIQUETAS_GRAVEDAD[resumen.gravedad] ?? resumen.gravedad}</span>}
          <OrigenNc origen={resumen.origen} />
          {resumen.visibleCliente !== undefined && <VisibilidadNc visible={resumen.visibleCliente} />}
        </span>
        <span className="pequena suave">{fechaHora(resumen.creadoEn)}</span>
      </button>
      <div className="nc-tarjeta-resumen">
        <p>{resumen.descripcion}</p>
        <p className="pequena tenue">
          {resumen.alcance === 'TODA_OT' ? 'Afecta a toda la OT' : 'Afecta a líneas concretas'}
          {resumen.creadoPorNombre !== null && ` · ${textoAutorNc(resumen)}`}
        </p>
      </div>
      {abierta && <DetalleNc idNc={resumen.id} onCambiada={onCambiada} />}
    </li>
  );
}

/**
 * Exportado en FASE 7.1: la página de Calidad (`/calidad/no-conformidades/:id`) reutiliza el MISMO detalle.
 *
 * F11.1 — de arriba abajo, lo que se pregunta al abrir una NC: qué es y dónde
 * (cabecera), qué falta para cerrarla y qué se puede hacer (cierre guiado),
 * cómo se está tratando (análisis y acciones), las pruebas (evidencias) y UN
 * único historial. La ficha de calidad se pide una sola vez para todo ello.
 */
export function DetalleNc({
  idNc,
  onCambiada,
}: {
  idNc: string;
  onCambiada: () => void;
}): ReactNode {
  const { usuario } = useSesion();
  const interno = usuario !== null && usuario.idCliente === null;
  const estado = useDatos(() => api.get<NcDetalle>(`/api/no-conformidades/${idNc}`), [idNc]);
  //  Sólo personal interno: el portal nunca la pide (el backend la niega a CLIENTE igualmente).
  const ficha = useDatos(
    () => (interno ? api.get<FichaCalidadNc>(`/api/no-conformidades/${idNc}/calidad`) : Promise.resolve(null)),
    [idNc, interno],
  );
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);

  if (estado.cargando && estado.datos === null) return <Cargando texto="Cargando detalle…" />;
  if (estado.error !== null) return <ErrorCaja error={estado.error} reintentar={estado.recargar} />;
  if (estado.datos === null) return null;

  const nc = estado.datos;
  const activa = nc.estado === 'ABIERTA' || nc.estado === 'EN_TRATAMIENTO';
  //  Capacidad YA existente en el backend (subirEvidenciaNcCliente, sin
  //  `evidencias:subir`): CLIENTE puede aportar evidencia sobre su PROPIA
  //  NC de origen CLIENTE. Aquí sólo se decide si aplica a ESTA NC — nunca
  //  se amplía el permiso, se refleja (encargo 4.4-D, sección 10).
  const permitirSubidaForzada = usuario !== null && usuario.idCliente !== null && nc.origen === 'CLIENTE';

  function recargarTodo(): void {
    estado.recargar();
    ficha.recargar();
    onCambiada();
  }

  function alTransicionar(estadoNuevo: string): void {
    setMensajeExito(`No conformidad ${nc.codigo} actualizada a «${etiquetaEstadoNc(estadoNuevo)}».`);
    recargarTodo();
  }

  return (
    <div className="nc-detalle">
      {mensajeExito !== null && (
        <div className="nota exito" role="status">
          {mensajeExito}
        </div>
      )}

      <CabeceraNc nc={nc} />

      {interno && <ClasificacionNc key={nc.gravedad ?? 'sin'} idNc={nc.id} gravedad={nc.gravedad} alCambiar={recargarTodo} />}

      {activa ? (
        <CierreGuiadoNc
          nc={nc}
          faltaParaCerrar={interno ? ficha.datos?.faltaParaCerrar ?? null : null}
          incidenciasAResolver={(ficha.datos?.incidencias ?? []).filter((i) => i.estado === 'ABIERTA' || i.estado === 'EN_ANALISIS')}
          alTransicionar={alTransicionar}
        />
      ) : (
        nc.estado === 'CERRADA' && (
          <div className="sep">
            {nc.cierreCliente != null ? (
              //  F11.3: cerrada por el cliente — no hay resolución interna; se dice quién, cuándo y por qué.
              <div className="nota" role="status">
                <strong>El cliente cerró la NC</strong>
                <div className="datos">
                  <Dato e="Cerrada por" v={nc.cierreCliente.autor} />
                  <Dato e="Fecha" v={fechaHora(nc.cierreCliente.ocurridoEn)} />
                  <Dato e="Motivo del cliente" v={nc.cierreCliente.motivo ?? '—'} />
                </div>
              </div>
            ) : (
              <div className="datos">
                <Dato e="Resolución" v={nc.accionCorrectiva ?? '—'} />
                <Dato e="Fecha de cierre" v={nc.fechaCierre === null ? '—' : fecha(nc.fechaCierre)} />
              </div>
            )}
            {interno && <ReabrirNc idNc={nc.id} permiso="nc:reabrir" alReabrir={() => alTransicionar('EN_TRATAMIENTO')} />}
          </div>
        )
      )}

      {interno && ficha.error !== null && <ErrorCaja error={ficha.error} reintentar={ficha.recargar} />}
      {interno && ficha.datos !== null && (
        <FichaCalidadNcSeccion idNc={nc.id} estadoNc={nc.estado} origenNc={nc.origen} ficha={ficha.datos} onCambiada={recargarTodo} />
      )}

      <div className="sep">
        {/*
          Ámbito NC explícito: sólo la evidencia vinculada a ESTA NC
          (id_no_conformidad = nc.id) — nunca la de la OT ni la de sus
          líneas (encargo 4.4-D, sección 3C).
        */}
        <Evidencias
          ambito="NC"
          ruta={`/api/no-conformidades/${nc.id}/evidencias`}
          permitirSubidaForzada={permitirSubidaForzada}
          titulo="Evidencias de la no conformidad"
        />
      </div>

      {/*
        F11.1: UN historial. La línea de tiempo de calidad ya incluye los
        cambios de estado (nc_historico_estado), las ediciones, las acciones y
        las evidencias; el antiguo «Histórico» sólo de estados la duplicaba.
      */}
      <div className="sep">
        <h3 className="titulo-seccion-nc">Historial</h3>
        {interno ? (
          ficha.datos === null ? <Cargando texto="Cargando historial…" /> : <LineaDeTiempo eventos={ficha.datos.lineaDeTiempo} sujeto="NC" />
        ) : (
          <HistorialEstadosNc nc={nc} />
        )}
      </div>
    </div>
  );
}

/** F11.1 — arriba del todo: dónde, quién, cuándo, para quién y de qué OT/línea viene. */
function CabeceraNc({ nc }: { nc: NcDetalle }): ReactNode {
  const antiguedad = nc.diasAbierta == null ? null : textoAntiguedadNc(nc);
  return (
    <div className="sep nc-cabecera">
      <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
        <OrigenNc origen={nc.origen} />
        {nc.visibleCliente !== undefined && <VisibilidadNc visible={nc.visibleCliente} />}
        {nc.vencida === true && <span className="etiqueta error">Vencida</span>}
        {nc.esperaRespuesta === true && <span className="etiqueta aviso">El cliente espera respuesta</span>}
      </div>
      <div className="datos">
        <Dato e="Centro" v={nc.centroNombre ?? '—'} />
        <Dato e="Ubicación" v={nc.ubicacionNombre ?? '—'} />
        <Dato e="Origen en OT" v={textoContextoNc(nc)} />
        <Dato e="Creada por" v={`${nc.creadoPorNombre ?? '—'}${nc.origen === 'CLIENTE' ? ' (cliente)' : ''}`} />
        <Dato e="Fecha de creación" v={`${fechaHora(nc.creadoEn)}${antiguedad === null ? '' : ` · ${antiguedad}`}`} />
        <Dato e="Responsable" v={nc.responsableNombre ?? 'Sin responsable'} />
        <Dato e="Fecha objetivo" v={nc.fechaObjetivo == null ? '—' : fecha(nc.fechaObjetivo)} />
        {textoPercepcionCliente(nc.impactoCliente, nc.urgenciaCliente) !== null && (
          <Dato e="Percepción del cliente" v={textoPercepcionCliente(nc.impactoCliente, nc.urgenciaCliente)!} />
        )}
        {nc.ultimaActividadCliente != null && <Dato e="Última actividad del cliente" v={fechaHora(nc.ultimaActividadCliente)} />}
      </div>
      {nc.lineas.length > 0 && (
        <div className="sep">
          <div className="etiqueta-dato">Líneas afectadas ({nc.lineas.length})</div>
          <ul className="lista-nc-lineas">
            {nc.lineas.map((l) => (
              <li key={l.id}>
                <span className="cod pequena">{l.ubicacionCodigo}</span>
                <span className="pequena tenue">{l.ubicacionRuta}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * F11.3 — reabrir una NC CERRADA (→ EN_TRATAMIENTO) con motivo obligatorio.
 * Lo usan la ficha interna (`nc:reabrir`) y el portal (`nc:reabrir_propia`):
 * misma ruta; la OT se recalcula sola con la lógica de siempre.
 */
export function ReabrirNc({ idNc, permiso, alReabrir }: { idNc: string; permiso: 'nc:reabrir' | 'nc:reabrir_propia'; alReabrir: () => void }): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!puede(permiso)) return null;

  async function enviar(): Promise<void> {
    if (motivo.trim() === '') {
      setError(new globalThis.Error('Explica por qué se reabre.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/no-conformidades/${idNc}/reabrir`, { motivo: motivo.trim(), idOperacionCliente });
      setAbierto(false);
      setMotivo('');
      alReabrir();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (!abierto) {
    return (
      <div className="lista-checks-horizontal" style={{ marginTop: 10 }}>
        <button type="button" className="pequeno" onClick={() => setAbierto(true)}>Reabrir</button>
      </div>
    );
  }
  return (
    <div className="panel accion-linea" role="region" aria-label="Reabrir">
      <div className="panel-cuerpo">
        <h4 style={{ margin: '0 0 4px' }}>Reabrir la no conformidad</h4>
        <p className="pequena suave" style={{ marginTop: 0 }}>Volverá a «En tratamiento». El motivo queda en el historial.</p>
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-m`}>Motivo de la reapertura</label>
          <textarea id={`${idBase}-m`} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>{enviando ? 'Reabriendo…' : 'Reabrir'}</button>
          <button type="button" className="pequeno" disabled={enviando} onClick={() => { setAbierto(false); setError(null); }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/** Sólo si quien mira no es personal interno (hoy nadie: el portal tiene su propia ficha). */
function HistorialEstadosNc({ nc }: { nc: NcDetalle }): ReactNode {
  return (
    <ul className="lista-nc-historial">
      {nc.historial.map((h) => (
        <li key={h.id}>
          <span className="pequena">
            {h.estadoAnterior === null
              ? `Creada · ${etiquetaEstadoNc('ABIERTA')}`
              : `${etiquetaEstadoNc(h.estadoAnterior)} → ${etiquetaEstadoNc(h.estadoNuevo)}`}
          </span>
          <span className="pequena tenue">
            {' '}
            · {h.actorNombre} · {fechaHora(h.ocurridoEn)}
          </span>
          {h.motivo !== null && h.motivo !== '' && <div className="pequena suave">{h.motivo}</div>}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- ciclo

type AccionNc = 'tratamiento' | 'cerrar' | 'anular';

/**
 * Hint de UX que refleja la máquina de `fn_nc_transicion` (0032):
 * ABIERTA -> EN_TRATAMIENTO|CERRADA|ANULADA; EN_TRATAMIENTO ->
 * CERRADA|ANULADA; CERRADA/ANULADA terminales. Nunca autoridad — el
 * servidor revalida siempre, exactamente igual que `accionesPermitidas`
 * de las líneas de OT (FASE 4.2-A/4.3-A).
 */
function puedeAccionNc(nc: NcDetalle, accion: AccionNc, puede: (p: string) => boolean): boolean {
  switch (accion) {
    case 'tratamiento':
      return puede('nc:tratar') && nc.estado === 'ABIERTA';
    case 'cerrar':
      return puede('nc:cerrar') && (nc.estado === 'ABIERTA' || nc.estado === 'EN_TRATAMIENTO');
    case 'anular':
      return puede('nc:anular') && (nc.estado === 'ABIERTA' || nc.estado === 'EN_TRATAMIENTO');
  }
}

const TITULO_ACCION_NC: Record<AccionNc, string> = {
  tratamiento: 'Iniciar tratamiento',
  cerrar: 'Cerrar',
  anular: 'Anular',
};

const RUTA_ACCION_NC: Record<AccionNc, string> = {
  tratamiento: 'iniciar-tratamiento',
  cerrar: 'cerrar',
  anular: 'anular',
};

/**
 * F11.1 — CIERRE GUIADO. Lo que falta para cerrar viene del servidor
 * (`faltaParaCerrar`, la misma definición que valida el cierre en
 * `calidad/cierre-nc.ts`): aquí no se calcula ninguna regla. Mientras falte
 * algo, «Cerrar» está deshabilitado y dice por qué; sin la ficha cargada
 * tampoco se ofrece (no se sabe todavía si se puede).
 */
function CierreGuiadoNc({
  nc,
  faltaParaCerrar,
  incidenciasAResolver,
  alTransicionar,
}: {
  nc: NcDetalle;
  faltaParaCerrar: readonly string[] | null;
  incidenciasAResolver: readonly { codigo: string }[];
  alTransicionar: (estadoNuevo: string) => void;
}): ReactNode {
  const { puede } = useSesion();
  const idNota = useId();
  const [accionAbierta, setAccionAbierta] = useState<AccionNc | null>(null);

  const disponibles = (['tratamiento', 'cerrar', 'anular'] as const).filter((a) => puedeAccionNc(nc, a, puede));
  if (disponibles.length === 0 && accionAbierta === null) return null;
  const listaParaCerrar = faltaParaCerrar !== null && faltaParaCerrar.length === 0;
  const informaCierre = disponibles.includes('cerrar') && faltaParaCerrar !== null;

  return (
    <div className="sep nc-cierre" role="region" aria-label="Estado y cierre">
      {informaCierre && accionAbierta === null && (
        faltaParaCerrar.length > 0 ? (
          <div className="nota aviso" role="note" id={idNota}>
            <strong>Para cerrar esta NC falta:</strong>
            <ul style={{ margin: '4px 0 0' }}>
              {faltaParaCerrar.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </div>
        ) : (
          <div className="nota exito" role="note" id={idNota}>
            Lista para cerrar: al cerrarla se pide la resolución.
          </div>
        )
      )}
      {accionAbierta === null ? (
        <div className="lista-checks-horizontal">
          {disponibles.map((a) => (
            <button
              key={a}
              type="button"
              className={a === 'cerrar' && listaParaCerrar ? 'principal' : 'pequeno'}
              disabled={a === 'cerrar' && !listaParaCerrar}
              aria-describedby={a === 'cerrar' && informaCierre ? idNota : undefined}
              onClick={() => setAccionAbierta(a)}
            >
              {TITULO_ACCION_NC[a]}
            </button>
          ))}
        </div>
      ) : (
        <FormularioTransicionNc
          idNc={nc.id}
          accion={accionAbierta}
          visibleCliente={nc.visibleCliente === true}
          incidenciasAResolver={incidenciasAResolver}
          cancelar={() => setAccionAbierta(null)}
          alExito={(estadoNuevo) => {
            setAccionAbierta(null);
            alTransicionar(estadoNuevo);
          }}
        />
      )}
    </div>
  );
}

function FormularioTransicionNc({
  idNc,
  accion,
  visibleCliente,
  incidenciasAResolver,
  cancelar,
  alExito,
}: {
  idNc: string;
  accion: AccionNc;
  visibleCliente: boolean;
  incidenciasAResolver: readonly { codigo: string }[];
  cancelar: () => void;
  alExito: (estadoNuevo: string) => void;
}): ReactNode {
  const idBase = useId();
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const requiereTexto = accion === 'cerrar' || accion === 'anular';
  //  FASE 7.2: al cerrar, informar (sin confirmación extra) de qué
  //  incidencias vinculadas pasarán solas a RESUELTA. Sólo personal interno.
  const aResolver = accion === 'cerrar' ? incidenciasAResolver : [];
  //  FASE 7.1: al cerrar se escribe la RESOLUCIÓN / conclusión — lo que se
  //  hizo ya está en las acciones correctivas (el campo sigue siendo
  //  `accionCorrectiva` en la API, mismo contrato que el modo offline).
  const etiquetaTexto = accion === 'cerrar' ? 'Resolución / conclusión' : 'Motivo';

  async function enviar(): Promise<void> {
    if (requiereTexto && texto.trim() === '') {
      setError(new globalThis.Error(`${etiquetaTexto} es obligatori${accion === 'cerrar' ? 'a' : 'o'}.`));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const datos: DatosTransicionNc =
        accion === 'cerrar'
          ? { accionCorrectiva: texto.trim(), idOperacionCliente }
          : { motivo: texto.trim() === '' ? null : texto.trim(), idOperacionCliente };
      const resultado = await api.post<ResultadoTransicionNc>(
        `/api/no-conformidades/${idNc}/${RUTA_ACCION_NC[accion]}`,
        datos,
      );
      alExito(resultado.estadoNuevo);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera">
        <h3>{TITULO_ACCION_NC[accion]}</h3>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {aResolver.length > 0 && (
          <div className="nota info" role="note">
            Al cerrar esta no conformidad,{' '}
            {aResolver.length === 1 ? '1 incidencia vinculada pasará' : `${String(aResolver.length)} incidencias vinculadas pasarán`} a
            RESUELTA: {aResolver.map((i) => i.codigo).join(', ')}.
          </div>
        )}
        {accion === 'cerrar' && visibleCliente && (
          <p className="pequena suave">Esta NC es visible para el cliente: verá la resolución que escribas.</p>
        )}
        <div className="campo">
          <label htmlFor={`${idBase}-texto`}>{etiquetaTexto}{requiereTexto ? '' : ' (opcional)'}</label>
          <textarea id={`${idBase}-texto`} value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} />
        </div>
        <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
          {enviando ? 'Enviando…' : TITULO_ACCION_NC[accion]}
        </button>
      </div>
    </div>
  );
}
